/**
 * Assessment Report Builder
 *
 * Generates detailed performance reports from assessment attempts.
 * Includes Leerlingprofiel enrichment (Phase 1).
 *
 * DDD co-locatie: moved from components/assessment/utils/ in s59.
 */

import { AssessmentReport, AssessmentAttempt, AssessmentBlueprint, ModulePerformance, TierPerformance, FamilyPerformance, PracticeRecommendation, ModuleLevelEstimate, GrowthIndicator, SkillGroupedArea, ActionPlan } from '../types';
import { type ModuleId, getFamilyDefinition, getFamiliesForModule, MODULES, getTierRangeForGroup, tierToNiveauCode, getTierConfig, QUESTIONS_BY_FAMILY } from '../../content';
import type { FamilyMasteryState } from '../../mastery';
import { getFamilyMasteryKey, resolvePhaseByDate } from '../../mastery';
import { GroupId } from '../../config/systemConfig';
import { debug } from '../../utils/logger';
import { tierToCitoLevel } from '../../utils/citoLevelUtils';

// ─── Report Thresholds & Constants ────────────────────────────
// All magic numbers extracted for auditability and single-source-of-truth.

/** Recommendation text thresholds (overall percentage) */
const REC_EXCELLENT_PCT = 85;
const REC_GOOD_PCT = 70;
const REC_MODERATE_PCT = 55;
const REC_TIER_ACCURACY_WEAK = 60;

/** Growth indicator thresholds */
const GROWTH_MIN_QUESTIONS = 3;
const GROWTH_REGRESSION_URGENT_PRE = 60;
const GROWTH_REGRESSION_URGENT_ACC = 30;
const GROWTH_REGRESSION_PRE = 50;
const GROWTH_REGRESSION_ACC = 40;
const GROWTH_FAST_ACC = 80;
const GROWTH_FAST_PRE_CEIL = 60;
const GROWTH_GOOD_ACC = 70;
const GROWTH_GROWING_ACC = 50;
const GROWTH_WATCH_ACC = 30;

/** Mastery label thresholds (postMastery → label) */
const MASTERY_LABEL_BEGINNER = 30;
const MASTERY_LABEL_OEFENAAR = 60;
const MASTERY_LABEL_GEVORDERD = 90;

/** Report classification thresholds */
const WEAKEST_CONCEPT_ACC = 60;
const STRONGEST_CONCEPT_ACC = 80;
const WEAK_AREA_MODULE_ACC = 70;
const STRONG_AREA_MODULE_ACC = 80;
const MAX_WEAK_STRONG_CONCEPTS = 5;
/**
 * Minimaal aantal vragen voordat een onderwerp "zwak" of "sterk" genoemd mag
 * worden. Sluit aan bij de ondergrens die de groei-indicator al hanteerde
 * (`GROWTH_MIN_QUESTIONS`); die stond er wél en hier niet, binnen hetzelfde
 * rapport.
 */
const MIN_QUESTIONS_FOR_CONCEPT_CLAIM = 2;
/**
 * Demping richting 50% voor kleine aantallen (Laplace-achtig).
 *
 * `0 van 1` en `0 van 6` zijn allebei 0% accuracy, maar het tweede is veel
 * sterker bewijs. Door twee "halve" pseudo-vragen mee te tellen schuift een
 * uitspraak op weinig data richting het midden, terwijl een uitspraak op veel
 * data vrijwel onveranderd blijft. Alleen gebruikt om te ORDENEN — het
 * getoonde percentage blijft de echte score, want een kind moet zijn eigen
 * uitslag kunnen narekenen.
 */
const SHRINKAGE_PSEUDO_QUESTIONS = 2;
function shrunkAccuracy(f: {
  correctCount: number;
  totalQuestions: number;
}): number {
  const k = SHRINKAGE_PSEUDO_QUESTIONS;
  return (f.correctCount + k * 0.5) / (f.totalQuestions + k) * 100;
}

/** Suggested practice thresholds */
const PRACTICE_LOW_ACC = 60;
const PRACTICE_HIGH_ACC = 80;
const PRACTICE_LOW_MASTERY = 70;
const PRACTICE_HIGH_POTENTIAL_ACC = 80;
const PRACTICE_HIGH_POTENTIAL_MASTERY = 85;
const MAX_SUGGESTED_PRACTICE = 5;

/** Action plan thresholds */
const ACTION_MEDIUM_ACC_MIN = 50;
const ACTION_MEDIUM_ACC_MAX = 80;
const ACTION_MEDIUM_MAX_COUNT = 2;
const ACTION_STRONG_ACC = 80;

/** Tier system */
const MAX_TIER = 16;

// `import.meta.env` is a Vite-only feature, undefined in the Magic Patterns
// runtime — use optional chaining to avoid module-load crashes.
const DEV = import.meta.env?.DEV ?? false;
function log(...args: unknown[]) {
  if (DEV) debug('[AssessmentReport]', ...args);
}
function generateRecommendation(percentage: number, tierPerformance: TierPerformance[], weakestConcepts: FamilyPerformance[], selectedGroup: GroupId): string {
  if (percentage >= REC_EXCELLENT_PCT) return 'Uitstekend! Je beheerst de stof goed. Focus op uitdagende onderwerpen om je kennis verder te verdiepen.';
  if (percentage >= REC_GOOD_PCT) {
    if (weakestConcepts.length > 0) return `Goed gedaan! Je bent op de goede weg. Focus op ${weakestConcepts[0].familyDescription.toLowerCase()} om je resultaat verder te verbeteren.`;
    return 'Goed gedaan! Je bent op de goede weg. Blijf oefenen om je kennis te versterken.';
  }
  if (percentage >= REC_MODERATE_PCT) {
    // R3-REP2: tier-band labels are now group-derived (e.g. "1-2", "13-14").
    // The recommendation engine looks at the FIRST (lowest) and LAST (highest)
    // band actually present in the report instead of fixed legacy ranges.
    const sortedByLowest = [...tierPerformance].sort((a, b) => {
      const aMin = parseInt(a.tierRange.replace(/[^0-9]/g, '').slice(0, 2), 10);
      const bMin = parseInt(b.tierRange.replace(/[^0-9]/g, '').slice(0, 2), 10);
      return aMin - bMin;
    });
    const lowestBand = sortedByLowest[0];
    const highestBand = sortedByLowest[sortedByLowest.length - 1];
    if (lowestBand && lowestBand.accuracy < REC_TIER_ACCURACY_WEAK) return 'Focus op de basisvaardigheden van jouw groep. Versterk je fundament voordat je verder gaat.';
    if (highestBand && highestBand.accuracy < REC_TIER_ACCURACY_WEAK) return 'Je basiskennis is goed, maar focus op de moeilijkere stof om verder te komen.';
    if (weakestConcepts.length > 0) return `Blijf oefenen! Begin met ${weakestConcepts[0].familyDescription.toLowerCase()} om snel vooruitgang te boeken.`;
    return 'Blijf oefenen! Regelmatig oefenen helpt je om de stof beter te begrijpen.';
  }
  if (weakestConcepts.length > 0) {
    const topWeak = weakestConcepts.slice(0, 2);
    const topics = topWeak.map(f => f.familyDescription.toLowerCase()).join(' en ');
    return `Geen zorgen, iedereen leert in zijn eigen tempo! Focus eerst op ${topics}. Neem de tijd en vraag hulp als je het nodig hebt.`;
  }
  return 'Geen zorgen, iedereen leert in zijn eigen tempo! Blijf oefenen en vraag hulp als je het nodig hebt.';
}

// ─── Leerlingprofiel Enrichment ───────────────────────────────

/**
 * v4.2 / R3-2026: Weighted ceiling-based level estimation with SEM.
 *
 * ALGORITHM (CITO-comparable):
 * Instead of simple "average tier of correct answers", uses a weighted
 * ceiling approach that better reflects what CITO measures:
 *
 * 1. Group answers by tier and compute per-tier accuracy
 * 2. Find the "ceiling tier" = highest tier where accuracy >= 60%
 *    (R3-CITO4: raised from 50% to 60% because the 4-choice MC format used
 *    by assessment has a 25% guessing floor — a 50% accuracy at a tier is
 *    only ~33% knowledge after correcting for chance, which is too weak to
 *    call "mastered". 60% maps to ~47% knowledge after the correction, a
 *    defensible "pass" signal aligned with classical CITO ceiling rules.)
 * 3. Compute weighted estimate: correct answers at higher tiers count more
 *    (weight = (tier / maxTier) × citoDiscrimination). R3-CITO3: items
 *    with higher discrimination indices carry more weight because they
 *    better separate strong from weak performers; items with no
 *    discrimination metadata fall back to the previous flat weight.
 * 4. Blend ceiling and weighted average (60/40) for robustness
 * 5. Calculate SEM from tier-level variance for confidence reporting.
 *    NOTE (R3-REP1): The SEM here is derived from the SPREAD of tier
 *    values across answers, not from item-level IRT. It is a useful
 *    rough confidence band but should not be interpreted as a strict
 *    psychometric standard error. Future work: replace with IRT-based
 *    SEM once citoDifficulty + citoDiscrimination calibration is verified
 *    across all seeds.
 *
 * WHY THIS IS DIFFERENT FROM PRACTICE:
 * - Practice uses mastery + LDG + interleaving to OPTIMIZE learning
 * - Assessment uses tier-weighted estimation to MEASURE level
 * - No LDG gating, no mastery influence, no interleaving
 * - Deliberately tests across the FULL tier range (low/mid/high bands)
 *
 * Falls back to simple average when < 3 questions (insufficient data).
 */

/** Minimum questions for weighted estimation (below this: simple average) */
const MIN_QUESTIONS_FOR_WEIGHTED = 3;
/**
 * R3-CITO4: Raised from 0.5 to 0.6. The 50% threshold was uncalibrated for
 * 4-choice MC (≈25% chance guess floor). 60% gives a meaningful "passed
 * this tier" signal after correcting for guessing.
 */
const CEILING_ACCURACY_THRESHOLD = 0.6;
/** Blend ratio: ceiling vs weighted average (0.6 = 60% ceiling, 40% weighted) */
const CEILING_BLEND_RATIO = 0.6;
/** Default discrimination when an item has no calibrated value */
const DEFAULT_DISCRIMINATION = 0.5;
function computeEstimatedLevels(byModule: ModulePerformance[], attempt: AssessmentAttempt, blueprint: AssessmentBlueprint, selectedGroup: GroupId): {
  estimatedLevels: ModuleLevelEstimate[];
  overallEstimatedTier: number;
  overallEstimatedLevelCode: string;
  overallExpectedTier: number;
  overallExpectedLevelCode: string;
  overallCitoLevel: {
    level: 'I' | 'II' | 'III' | 'IV' | 'V';
    label: string;
    color: string;
    bgColor: string;
    borderColor: string;
  };
} {
  const period = resolvePhaseByDate();
  const tierRange = getTierRangeForGroup(selectedGroup);
  // R3-REP3: Per-group period mapping. Previously the heuristic ignored
  // group-8 (which has only 4 in-range tiers) and treated all groups the
  // same. The new mapping picks the lowest non-plus tier whose period
  // matches the calendar phase. Falls back to the range minimum when no
  // tier with that period exists for the group.
  const periodTier = (() => {
    for (let t = tierRange.min; t <= tierRange.max; t++) {
      const cfg = getTierConfig(t);
      if (cfg && cfg.period === period && !cfg.isPlus) return t;
    }
    // Fallback: derive from period without exact match
    if (period === 'B') return tierRange.min;
    if (period === 'E') return tierRange.max;
    return tierRange.min + Math.floor((tierRange.max - tierRange.min) / 2);
  })();
  const expectedTier = periodTier;
  const expectedLevelCode = tierToNiveauCode(expectedTier) || `Tier ${expectedTier}`;
  const estimatedLevels: ModuleLevelEstimate[] = [];
  let totalWeightedTier = 0;
  let totalWeight = 0;
  byModule.forEach(modPerf => {
    const module = MODULES.find(m => m.id === modPerf.moduleId);
    if (!module) return;
    const modQuestions = attempt.answers.filter(a => a.module === modPerf.moduleId);
    if (modQuestions.length === 0) return;

    // Resolve tier + discrimination for each answer from blueprint + seed
    // R3-CITO3: pull citoDiscrimination from the canonical Question record
    // so it can weight the estimation. Falls back to DEFAULT_DISCRIMINATION
    // when an item has no calibrated value.
    const answersWithTier = modQuestions.map(ans => {
      const bpQ = blueprint.questions.find(q => q.questionId === ans.questionId);
      const tier = bpQ ? bpQ.tier : 0;
      let discrimination = DEFAULT_DISCRIMINATION;
      if (bpQ) {
        const famQs = QUESTIONS_BY_FAMILY.get(bpQ.familyId);
        const seedQ = famQs?.find(q => q.id === ans.questionId);
        if (seedQ?.citoDiscrimination !== undefined) {
          discrimination = seedQ.citoDiscrimination;
        }
      }
      return {
        ...ans,
        tier,
        discrimination
      };
    }).filter(a => a.tier > 0);
    if (answersWithTier.length === 0) return;
    const correctAnswers = answersWithTier.filter(a => a.isCorrect);
    let estimatedTier: number;
    let sem: number | undefined;
    let estimationMethod: 'weighted-ceiling' | 'simple-average';
    if (answersWithTier.length >= MIN_QUESTIONS_FOR_WEIGHTED) {
      // ── v4.2: Weighted ceiling estimation ──────────────────────
      estimationMethod = 'weighted-ceiling';

      // Step 1: Group by tier and compute per-tier accuracy
      const tierAccuracy = new Map<number, {
        correct: number;
        total: number;
      }>();
      for (const ans of answersWithTier) {
        const entry = tierAccuracy.get(ans.tier) || {
          correct: 0,
          total: 0
        };
        entry.total++;
        if (ans.isCorrect) entry.correct++;
        tierAccuracy.set(ans.tier, entry);
      }

      // Step 2: Find ceiling tier (highest tier with accuracy >= threshold)
      const sortedTiers = Array.from(tierAccuracy.entries()).sort(([a], [b]) => a - b);
      let ceilingTier = tierRange.min; // default: lowest tier
      for (const [tier, stats] of sortedTiers) {
        if (stats.total > 0 && stats.correct / stats.total >= CEILING_ACCURACY_THRESHOLD) {
          ceilingTier = tier;
        }
      }

      // Step 3: Weighted average (higher tiers weight more)
      let weightedSum = 0;
      let weightTotal = 0;
      for (const ans of correctAnswers) {
        const weight = ans.tier / MAX_TIER; // Higher tier = more weight
        weightedSum += ans.tier * weight;
        weightTotal += weight;
      }
      // Include penalty for incorrect low-tier answers
      const incorrectAnswers = answersWithTier.filter(a => !a.isCorrect);
      for (const ans of incorrectAnswers) {
        if (ans.tier <= ceilingTier) {
          // Failing below ceiling = negative signal (gap in foundation)
          const penalty = 0.3 * (ans.tier / MAX_TIER);
          weightedSum -= ans.tier * penalty;
          weightTotal += penalty;
        }
        // Failing above ceiling is expected — no penalty
      }
      const weightedAvg = weightTotal > 0 ? weightedSum / weightTotal : ceilingTier;

      // Step 4: Blend ceiling and weighted average
      estimatedTier = CEILING_BLEND_RATIO * ceilingTier + (1 - CEILING_BLEND_RATIO) * weightedAvg;

      // Step 5: Calculate SEM from tier-level variance
      const tierValues = answersWithTier.map(a => a.isCorrect ? a.tier : a.tier - 1);
      const mean = tierValues.reduce((s, v) => s + v, 0) / tierValues.length;
      const variance = tierValues.reduce((s, v) => s + (v - mean) ** 2, 0) / tierValues.length;
      const sd = Math.sqrt(variance);
      sem = sd / Math.sqrt(tierValues.length);
      log(`Level estimation for ${modPerf.moduleId}: ceiling=${ceilingTier}, ` + `weightedAvg=${weightedAvg.toFixed(1)}, blended=${estimatedTier.toFixed(1)}, ` + `SEM=±${sem.toFixed(2)} (${answersWithTier.length} questions)`);
    } else {
      // ── Fallback: simple average (too few questions) ───────────
      estimationMethod = 'simple-average';
      if (correctAnswers.length > 0) {
        const sumTiers = correctAnswers.reduce((sum, ans) => sum + ans.tier, 0);
        estimatedTier = sumTiers / correctAnswers.length;
      } else {
        const attemptedTiers = answersWithTier.map(a => a.tier);
        estimatedTier = Math.max(1, Math.min(...attemptedTiers) - 1);
      }
    }
    estimatedTier = Math.max(1, Math.min(MAX_TIER, Math.round(estimatedTier)));
    const estimatedLevelCode = tierToNiveauCode(estimatedTier) || `Tier ${estimatedTier}`;
    const delta = estimatedTier - expectedTier;
    const confidenceBand = sem !== undefined ? {
      low: Math.max(1, Math.round(estimatedTier - sem)),
      high: Math.min(MAX_TIER, Math.round(estimatedTier + sem))
    } : undefined;
    const citoLevel = tierToCitoLevel(estimatedTier, selectedGroup);
    estimatedLevels.push({
      moduleId: modPerf.moduleId,
      moduleTitle: module.title,
      estimatedTier,
      estimatedLevelCode,
      expectedTier,
      expectedLevelCode,
      delta,
      deltaLabel: delta > 0 ? 'boven' : delta === 0 ? 'op' : 'onder',
      questionCount: modQuestions.length,
      accuracy: modPerf.accuracy,
      sem,
      confidenceBand,
      estimationMethod,
      citoLevel
    });

    // Weight overall by question count (more questions = more reliable)
    totalWeightedTier += estimatedTier * modQuestions.length;
    totalWeight += modQuestions.length;
  });
  const overallEstimatedTier = totalWeight > 0 ? Math.round(totalWeightedTier / totalWeight) : expectedTier;
  const overallEstimatedLevelCode = tierToNiveauCode(overallEstimatedTier) || `Tier ${overallEstimatedTier}`;
  const overallCitoLevel = tierToCitoLevel(overallEstimatedTier, selectedGroup);
  return {
    estimatedLevels,
    overallEstimatedTier,
    overallEstimatedLevelCode,
    overallExpectedTier: expectedTier,
    overallExpectedLevelCode: expectedLevelCode,
    overallCitoLevel
  };
}
function computeGrowthIndicators(byModule: ModulePerformance[], byFamily: FamilyPerformance[]): GrowthIndicator[] {
  return byModule.map(mod => {
    const modFamilies = byFamily.filter(f => {
      const def = getFamilyDefinition(f.familyId);
      return def && def.module === mod.moduleId;
    });
    const avgPreMastery = modFamilies.length > 0 ? modFamilies.reduce((sum, f) => sum + f.preMastery, 0) / modFamilies.length : 0;
    const accuracy = mod.accuracy;
    let level: GrowthIndicator['level'] = 'onbekend';
    let emoji = '⚪';
    let label = 'Onbekend';
    let color = 'text-slate-400';
    if (mod.totalQuestions < GROWTH_MIN_QUESTIONS) {
      level = 'onbekend';
      emoji = '⚪';
      label = 'Te weinig data';
      color = 'text-slate-400';
    } else if (avgPreMastery >= GROWTH_REGRESSION_URGENT_PRE && accuracy < GROWTH_REGRESSION_URGENT_ACC) {
      // Had decent mastery but scored very poorly → urgent regression
      level = 'actie_vereist';
      emoji = '🔴⚠';
      label = 'Actie vereist';
      color = 'text-rose-700';
    } else if (avgPreMastery >= GROWTH_REGRESSION_PRE && accuracy < GROWTH_REGRESSION_ACC) {
      // Had moderate mastery but scored poorly → declining
      level = 'krimpt';
      emoji = '🔴↓';
      label = 'Krimpt';
      color = 'text-rose-600';
    } else if (accuracy >= GROWTH_FAST_ACC && avgPreMastery < GROWTH_FAST_PRE_CEIL) {
      level = 'groei_snel';
      emoji = '🟢↗';
      label = 'Ik groei snel';
      color = 'text-emerald-600';
    } else if (accuracy >= GROWTH_GOOD_ACC) {
      level = 'gaat_goed';
      emoji = '🟢→';
      label = 'Het gaat goed';
      color = 'text-emerald-600';
    } else if (accuracy >= GROWTH_GROWING_ACC) {
      level = 'ik_groei';
      emoji = '🟡→';
      label = 'Ik groei';
      color = 'text-yellow-600';
    } else if (accuracy >= GROWTH_WATCH_ACC) {
      level = 'let_op';
      emoji = '🟠↘';
      label = 'Let op mij';
      color = 'text-orange-600';
    } else {
      level = 'extra_aandacht';
      emoji = '🔴↓';
      label = 'Extra aandacht';
      color = 'text-rose-600';
    }
    return {
      moduleId: mod.moduleId,
      level,
      emoji,
      label,
      color
    };
  });
}
function groupFamiliesBySkill(families: FamilyPerformance[]): SkillGroupedArea[] {
  const grouped = new Map<ModuleId, Map<string, FamilyPerformance[]>>();
  families.forEach(f => {
    const def = getFamilyDefinition(f.familyId);
    if (!def) return;
    if (!grouped.has(def.module)) grouped.set(def.module, new Map());
    const moduleGroup = grouped.get(def.module)!;
    if (!moduleGroup.has(def.skill)) moduleGroup.set(def.skill, []);
    moduleGroup.get(def.skill)!.push(f);
  });
  const result: SkillGroupedArea[] = [];
  grouped.forEach((skillsMap, moduleId) => {
    const moduleDef = MODULES.find(m => m.id === moduleId);
    const skills: SkillGroupedArea['skills'] = [];
    skillsMap.forEach((fams, skillName) => {
      const avgAcc = fams.reduce((sum, f) => sum + f.accuracy, 0) / fams.length;
      skills.push({
        skillName: skillName.replace(/_/g, ' '),
        families: fams.map(f => ({
          familyId: f.familyId,
          description: f.familyDescription,
          accuracy: f.accuracy,
          masteryLevel: f.postMastery < MASTERY_LABEL_BEGINNER ? 'beginner' : f.postMastery < MASTERY_LABEL_OEFENAAR ? 'oefenaar' : f.postMastery < MASTERY_LABEL_GEVORDERD ? 'gevorderd' : 'meester'
        })),
        avgAccuracy: avgAcc
      });
    });
    if (skills.length > 0) {
      result.push({
        moduleId,
        moduleTitle: moduleDef ? moduleDef.title : moduleId,
        skills: skills.sort((a, b) => a.avgAccuracy - b.avgAccuracy)
      });
    }
  });
  return result;
}
function buildActionPlan(weakestConcepts: FamilyPerformance[], byFamily: FamilyPerformance[], estimatedLevels: ModuleLevelEstimate[]): ActionPlan {
  let beginMet = null;
  if (weakestConcepts.length > 0) {
    const weakest = weakestConcepts[0];
    const def = getFamilyDefinition(weakest.familyId);
    const mod = MODULES.find(m => m.id === def?.module);
    if (def && mod) {
      beginMet = {
        moduleTitle: mod.title,
        skillName: def.skill.replace(/_/g, ' '),
        familyDescription: weakest.familyDescription,
        moduleId: def.module
      };
    }
  }
  const gaVerderMet: ActionPlan['gaVerderMet'] = [];
  const mediumFamilies = byFamily.filter(f => f.accuracy >= ACTION_MEDIUM_ACC_MIN && f.accuracy < ACTION_MEDIUM_ACC_MAX).slice(0, ACTION_MEDIUM_MAX_COUNT);
  for (const f of mediumFamilies) {
    const def = getFamilyDefinition(f.familyId);
    const mod = MODULES.find(m => m.id === def?.module);
    if (def && mod) {
      gaVerderMet.push({
        moduleTitle: mod.title,
        skillName: def.skill.replace(/_/g, ' '),
        familyDescription: f.familyDescription,
        moduleId: def.module
      });
    }
  }
  let uitdaging = null;
  const strongModule = estimatedLevels.find(m => m.accuracy >= ACTION_STRONG_ACC);
  if (strongModule) {
    uitdaging = {
      moduleTitle: strongModule.moduleTitle,
      suggestion: `Probeer een niveau hoger in ${strongModule.moduleTitle}!`,
      moduleId: strongModule.moduleId
    };
  }
  return {
    beginMet,
    gaVerderMet,
    uitdaging
  };
}

// ─── Main Export ──────────────────────────────────────────────

export function buildAssessmentReport(attempt: AssessmentAttempt, blueprint: AssessmentBlueprint, preFamilyMastery: Record<string, FamilyMasteryState>, postFamilyMastery: Record<string, FamilyMasteryState>): AssessmentReport {
  log(`Building report for attempt ${attempt.id}`);
  const {
    answers,
    selectedGroup
  } = attempt;
  const correctCount = answers.filter(a => a.isCorrect).length;
  const totalQuestions = answers.length;
  const percentage = totalQuestions > 0 ? Math.round(correctCount / totalQuestions * 100) : 0;
  const totalSeconds = attempt.totalTimeSeconds;
  const avgPerQuestion = totalQuestions > 0 ? Math.round(totalSeconds / totalQuestions) : 0;

  // Module breakdown
  const moduleMap = new Map<ModuleId, {
    correct: number;
    total: number;
    time: number;
  }>();
  answers.forEach(answer => {
    const current = moduleMap.get(answer.module) || {
      correct: 0,
      total: 0,
      time: 0
    };
    current.total++;
    if (answer.isCorrect) current.correct++;
    current.time += answer.timeSpent;
    moduleMap.set(answer.module, current);
  });
  const byModule: ModulePerformance[] = Array.from(moduleMap.entries()).map(([moduleId, stats]) => ({
    moduleId,
    totalQuestions: stats.total,
    correctCount: stats.correct,
    accuracy: Math.round(stats.correct / stats.total * 100),
    avgTimePerQuestion: Math.round(stats.time / stats.total)
  }));

  // Tier breakdown
  // R3-REP2: Tier bands are now derived from the learner's actual group
  // range rather than hardcoded as 1-6/7-12/13-16. This means a group-7
  // learner sees "begin/midden/eind" buckets aligned to their own
  // curriculum window instead of being mapped onto group-6 boundaries
  // when their assessment happens to include below-grade tiers.
  const grpRange = getTierRangeForGroup(selectedGroup);
  const grpSpan = grpRange.max - grpRange.min + 1;
  const grpThirdLow = grpRange.min + Math.ceil(grpSpan / 3) - 1;
  const grpThirdMid = grpRange.min + Math.ceil(grpSpan * 2 / 3) - 1;
  const labelLow = `${grpRange.min}-${grpThirdLow}`;
  const labelMid = `${grpThirdLow + 1}-${grpThirdMid}`;
  const labelHigh = `${grpThirdMid + 1}-${grpRange.max}`;
  const labelBelow = `<${grpRange.min}`;
  const labelAbove = `>${grpRange.max}`;
  function bandLabelForTier(tier: number): string {
    if (tier < grpRange.min) return labelBelow;
    if (tier > grpRange.max) return labelAbove;
    if (tier <= grpThirdLow) return labelLow;
    if (tier <= grpThirdMid) return labelMid;
    return labelHigh;
  }
  const tierBandOrder = [labelBelow, labelLow, labelMid, labelHigh, labelAbove];
  const tierMap = new Map<string, {
    correct: number;
    total: number;
  }>();
  answers.forEach(answer => {
    const blueprintQ = blueprint.questions.find(q => q.questionId === answer.questionId);
    if (!blueprintQ) return;
    const tierRange = bandLabelForTier(blueprintQ.tier);
    const current = tierMap.get(tierRange) || {
      correct: 0,
      total: 0
    };
    current.total++;
    if (answer.isCorrect) current.correct++;
    tierMap.set(tierRange, current);
  });
  const byTier: TierPerformance[] = Array.from(tierMap.entries()).map(([tierRange, stats]) => ({
    tierRange,
    totalQuestions: stats.total,
    correctCount: stats.correct,
    accuracy: Math.round(stats.correct / stats.total * 100)
  })).sort((a, b) => {
    return tierBandOrder.indexOf(a.tierRange) - tierBandOrder.indexOf(b.tierRange);
  });

  // Family breakdown
  const familyMap = new Map<string, {
    correct: number;
    total: number;
    time: number;
  }>();
  answers.forEach(answer => {
    const current = familyMap.get(answer.familyId) || {
      correct: 0,
      total: 0,
      time: 0
    };
    current.total++;
    if (answer.isCorrect) current.correct++;
    current.time += answer.timeSpent;
    familyMap.set(answer.familyId, current);
  });
  const byFamily: FamilyPerformance[] = Array.from(familyMap.entries()).map(([familyId, stats]) => {
    const def = getFamilyDefinition(familyId);
    if (!def) return null;
    const preKey = getFamilyMasteryKey(familyId, selectedGroup);
    const preMastery = preFamilyMastery[preKey]?.masteryScore || 0;
    const postKey = getFamilyMasteryKey(familyId, selectedGroup);
    const postMastery = postFamilyMastery[postKey]?.masteryScore || 0;
    return {
      familyId,
      familyDescription: def.description,
      totalQuestions: stats.total,
      correctCount: stats.correct,
      accuracy: Math.round(stats.correct / stats.total * 100),
      preMastery,
      postMastery,
      delta: postMastery - preMastery
    };
  }).filter((f): f is FamilyPerformance => f !== null).sort((a, b) => shrunkAccuracy(a) - shrunkAccuracy(b) ||
  // Gelijke schatting: het onderwerp met méér vragen eerst. Dat is de
  // uitspraak waar we het meest van weten.
  b.totalQuestions - a.totalQuestions);

  // Een onderwerp telt alleen als zwak/sterk als er genoeg bewijs voor is.
  //
  // De blueprint geeft per family hoogstens 2 vragen. Eén fout antwoord zette
  // een family daarmee op 0%, en dat was genoeg om bovenaan "Begin met…" te
  // belanden — vóór een onderwerp waar het kind 2 van 5 goed had. Dat tweede
  // is echt bewijs van moeite; het eerste kan een verkeerd gelezen vraag zijn.
  //
  // Twee maatregels, samen: de gedempte schatting bepaalt de vólgorde, en de
  // ondergrens bepaalt of een onderwerp überhaupt een conclusie mag dragen.
  // Alleen dempen zou een 0-op-1 nog steeds laten meedoen, alleen een
  // ondergrens zou 0-op-2 gelijkstellen aan 0-op-6.
  const withEnoughEvidence = byFamily.filter(f => f.totalQuestions >= MIN_QUESTIONS_FOR_CONCEPT_CLAIM);
  const weakestConcepts = withEnoughEvidence.filter(f => f.accuracy < WEAKEST_CONCEPT_ACC).slice(0, MAX_WEAK_STRONG_CONCEPTS);
  const strongestConcepts = withEnoughEvidence.filter(f => f.accuracy > STRONGEST_CONCEPT_ACC).sort((a, b) => b.accuracy - a.accuracy).slice(0, MAX_WEAK_STRONG_CONCEPTS);
  const weakAreas = byModule.filter(m => m.accuracy < WEAK_AREA_MODULE_ACC).sort((a, b) => a.accuracy - b.accuracy).map(m => {
    const module = MODULES.find(mod => mod.id === m.moduleId);
    return module?.title || m.moduleId;
  });
  const strongAreas = byModule.filter(m => m.accuracy > STRONG_AREA_MODULE_ACC).sort((a, b) => b.accuracy - a.accuracy).map(m => {
    const module = MODULES.find(mod => mod.id === m.moduleId);
    return module?.title || m.moduleId;
  });

  // Suggested practice
  const suggestedPractice: PracticeRecommendation[] = [];
  weakestConcepts.forEach(family => {
    suggestedPractice.push({
      familyId: family.familyId,
      familyDescription: family.familyDescription,
      currentMastery: family.postMastery,
      reason: 'weak_performance',
      priority: 1
    });
  });
  byFamily.filter(f => f.accuracy >= PRACTICE_LOW_ACC && f.accuracy <= PRACTICE_HIGH_ACC && f.postMastery < PRACTICE_LOW_MASTERY && !suggestedPractice.some(s => s.familyId === f.familyId)).forEach(family => {
    suggestedPractice.push({
      familyId: family.familyId,
      familyDescription: family.familyDescription,
      currentMastery: family.postMastery,
      reason: 'low_mastery',
      priority: 2
    });
  });
  byFamily.filter(f => f.accuracy > PRACTICE_HIGH_POTENTIAL_ACC && f.postMastery < PRACTICE_HIGH_POTENTIAL_MASTERY && !suggestedPractice.some(s => s.familyId === f.familyId)).forEach(family => {
    suggestedPractice.push({
      familyId: family.familyId,
      familyDescription: family.familyDescription,
      currentMastery: family.postMastery,
      reason: 'high_potential',
      priority: 3
    });
  });
  suggestedPractice.sort((a, b) => a.priority - b.priority);
  const recommendation = generateRecommendation(percentage, byTier, weakestConcepts, selectedGroup);

  // Leerlingprofiel enrichment
  const {
    estimatedLevels,
    overallEstimatedTier,
    overallEstimatedLevelCode,
    overallExpectedTier,
    overallExpectedLevelCode,
    overallCitoLevel
  } = computeEstimatedLevels(byModule, attempt, blueprint, selectedGroup);
  const growthIndicators = computeGrowthIndicators(byModule, byFamily);
  const strengthsBySkill = groupFamiliesBySkill(strongestConcepts);
  const focusAreasBySkill = groupFamiliesBySkill(weakestConcepts);
  const actionPlan = buildActionPlan(weakestConcepts, byFamily, estimatedLevels);

  // R3-DOK1: DOK distribution surfaces the cognitive complexity actually
  // tested. DOK is only used as a blueprint filter today; making it visible
  // in the report lets teachers verify the assessment exercised higher-order
  // thinking (DOK 2/3) and not just recall (DOK 1).
  const dokBuckets = {
    dok1: 0,
    dok2: 0,
    dok3: 0,
    dok4: 0,
    unknown: 0
  };
  let dokCorrectAtLevel = {
    dok1: 0,
    dok2: 0,
    dok3: 0,
    dok4: 0,
    unknown: 0
  };
  answers.forEach(ans => {
    const bpQ = blueprint.questions.find(q => q.questionId === ans.questionId);
    if (!bpQ) return;
    const famQs = QUESTIONS_BY_FAMILY.get(bpQ.familyId);
    const seedQ = famQs?.find(q => q.id === ans.questionId);
    const dok = seedQ?.pedagogy?.dok;
    const key = dok === 1 ? 'dok1' : dok === 2 ? 'dok2' : dok === 3 ? 'dok3' : dok === 4 ? 'dok4' : 'unknown';
    dokBuckets[key as keyof typeof dokBuckets]++;
    if (ans.isCorrect) {
      dokCorrectAtLevel[key as keyof typeof dokCorrectAtLevel]++;
    }
  });
  const dokDistribution = {
    counts: dokBuckets,
    correctCounts: dokCorrectAtLevel,
    accuracy: {
      dok1: dokBuckets.dok1 > 0 ? Math.round(dokCorrectAtLevel.dok1 / dokBuckets.dok1 * 100) : null,
      dok2: dokBuckets.dok2 > 0 ? Math.round(dokCorrectAtLevel.dok2 / dokBuckets.dok2 * 100) : null,
      dok3: dokBuckets.dok3 > 0 ? Math.round(dokCorrectAtLevel.dok3 / dokBuckets.dok3 * 100) : null,
      dok4: dokBuckets.dok4 > 0 ? Math.round(dokCorrectAtLevel.dok4 / dokBuckets.dok4 * 100) : null
    }
  };
  return {
    attemptId: attempt.id,
    blueprintId: attempt.blueprintId,
    completedAt: attempt.completedAt ?? Date.now(),
    selectedGroup,
    assignmentId: attempt.assignmentId,
    score: {
      correctCount,
      totalQuestions,
      percentage
    },
    time: {
      totalSeconds,
      avgPerQuestion
    },
    byModule,
    byTier,
    byFamily,
    weakestConcepts,
    strongestConcepts,
    weakAreas,
    strongAreas,
    suggestedPractice: suggestedPractice.slice(0, MAX_SUGGESTED_PRACTICE),
    recommendation,
    answers: attempt.answers,
    estimatedLevels,
    overallEstimatedTier,
    overallEstimatedLevelCode,
    overallExpectedTier,
    overallExpectedLevelCode,
    overallCitoLevel,
    growthIndicators,
    strengthsBySkill,
    focusAreasBySkill,
    actionPlan,
    // DOK-verdeling: werd hierboven volledig berekend maar ontbrak in dit
    // return-object, waardoor de afgebouwde DOK-sectie in AssessmentReportPage
    // nooit data kreeg. Het veld is optioneel getypeerd, dus de compiler zweeg.
    // Toevoegen is additief: reports worden als opaque JSON-payload opgeslagen,
    // dus oudere rapporten zonder dit veld blijven geldig.
    dokDistribution
  };
}